import datePickerActions from '../actions/datePickerActions';
import listingActions from '../actions/listingActions';
import tripActions from '../actions/tripActions';
import {OpenModalStates} from '../records/listingPageRecord';
import {DatePickerStates} from '../utils/datePickerVals';
import {
    getMaxAdultsFromHouseRules,
    getMaxChildrenFromHouseRules,
    hasValidGuests
} from '../utils/guests';
import {getTravelerSegmentFeatures} from '../selectors/travelerSegmentFeatures';
import {isInquiryModalOpen} from '../selectors/modalStates';

function getNextOpenDatePickerState(arriveDateText, departDateText) {
    return (
        (!arriveDateText && !departDateText && DatePickerStates.START) ||
        (!!arriveDateText && !!departDateText && DatePickerStates.SOFT_START) ||
        (!!arriveDateText && !departDateText && DatePickerStates.END) ||
        null
    );
}

function datePickerStateChanged(datePickerReducer, tripDetailsReducer) {
    const arriveDateChanged =
        datePickerReducer.arriveDateText !== tripDetailsReducer.arriveDateText;
    const departDateChanged =
        datePickerReducer.departDateText !== tripDetailsReducer.departDateText;

    return arriveDateChanged || departDateChanged;
}

function getNextOpenModalState(state) {
    const currentModalState = state.listingPageReducer.openModalState;
    const isBookingFlow = state.tripDetailsReducer.isBookingFlow;

    if (isBookingFlow) {
        return OpenModalStates.BOOKING_FLOW_DATE_PICKER;
    }

    const nextStates = {
        [OpenModalStates.NONE]: OpenModalStates.MAIN_PAGE_DATE_PICKER,
        [OpenModalStates.REQUEST_TO_BOOK]: OpenModalStates.REQUEST_TO_BOOK_DATE_PICKER,
        [OpenModalStates.INQUIRY]: OpenModalStates.INQUIRY_DATE_PICKER
    };

    return nextStates[currentModalState] || currentModalState;
}

function getNextCloseModalState(state, isCancel) {
    const currentModalState = state.listingPageReducer.openModalState;
    const isBookingFlow = state.tripDetailsReducer.isBookingFlow;
    const {isMobile} = state.systemReducer;

    if (isBookingFlow) {
        if (isMobile) {
            return isCancel ? OpenModalStates.NONE : OpenModalStates.REQUEST_TO_BOOK;
        }

        return OpenModalStates.NONE;
    }

    const nextStates = {
        [OpenModalStates.MAIN_PAGE_DATE_PICKER]: OpenModalStates.NONE,
        [OpenModalStates.REQUEST_TO_BOOK_DATE_PICKER]: OpenModalStates.REQUEST_TO_BOOK,
        [OpenModalStates.INQUIRY_DATE_PICKER]: OpenModalStates.INQUIRY
    };

    return nextStates[currentModalState] || currentModalState;
}

function openDatePickerModal(store) {
    const state = store.getState();
    const currentDatePickerState = state.datePickerReducer.datePickerState;
    const tripState = state.tripDetailsReducer;
    const arriveDateText = tripState.arriveDateText;
    const departDateText = tripState.departDateText;

    const nextModalState = getNextOpenModalState(state);

    // Initialize the date picker record with the currently selected dates.
    store.dispatch(
        datePickerActions.changedSelectedDates({
            arriveDateText,
            departDateText
        })
    );

    store.dispatch(listingActions.updateModalState(nextModalState));

    const nextDatePickerState = getNextOpenDatePickerState(arriveDateText, departDateText);

    store.dispatch(
        datePickerActions.updateDatePickerState({
            datePickerPrevState: currentDatePickerState || DatePickerStates.CLOSED,
            datePickerState: nextDatePickerState
        })
    );
}

function checkGuests(store) {
    const state = store.getState();
    const listing = state.listingReducer;
    const tripDetails = state.tripDetailsReducer;
    const {houseRules, sleeps} = listing;

    const maxAdults = getMaxAdultsFromHouseRules({houseRules, sleeps});
    const maxChildren = getMaxChildrenFromHouseRules({houseRules, sleeps});

    const validGuests = hasValidGuests({
        adults: tripDetails.adults,
        children: tripDetails.children,
        hasPets: tripDetails.hasPets,
        maxAdults,
        maxChildren,
        sleeps: listing.sleeps,
        petsAllowed: listing.petsAllowed,
        isEmptyValid: false
    });

    if (!validGuests) {
        store.dispatch(listingActions.openGuestSelectorModal());
    }
}

function closeDatePickerModal(store, action = {payload: {}}) {
    const {isCancel} = action.payload;

    const state = store.getState();
    const datePickerData = state.datePickerReducer;
    const datePickerPrevState = datePickerData.datePickerState;
    const system = state.systemReducer;
    const isMobile = system.isMobile;

    const nextModalState = getNextCloseModalState(state, isCancel);

    store.dispatch(listingActions.updateModalState(nextModalState));

    store.dispatch(
        datePickerActions.updateDatePickerState({
            datePickerPrevState,
            datePickerState: DatePickerStates.CLOSED
        })
    );

    const {redirectToPinnedSerpOnDateEntry} = getTravelerSegmentFeatures(state);
    const shouldCheckGuests =
        !redirectToPinnedSerpOnDateEntry ||
        (redirectToPinnedSerpOnDateEntry && isInquiryModalOpen(state));

    if (isCancel) {
        store.dispatch(tripActions.setBookingFlow(false));
    } else if (isMobile && shouldCheckGuests) {
        checkGuests(store);
    }
}

function changedSelectedDates(store, action = {payload: {}}) {
    // set datepicker to correct state
    const state = store.getState();
    const datePickerData = state.datePickerReducer;
    const datePickerPrevState = datePickerData.datePickerState;
    const {fieldsChanged} = action.payload;

    const nextDatePickerState =
        (!fieldsChanged.arriveDateText &&
            !fieldsChanged.departDateText &&
            DatePickerStates.START) ||
        (fieldsChanged.arriveDateText && !fieldsChanged.departDateText && DatePickerStates.END) ||
        (fieldsChanged.arriveDateText && fieldsChanged.departDateText && DatePickerStates.DONE) ||
        null;

    store.dispatch(
        datePickerActions.updateDatePickerState({
            datePickerPrevState,
            datePickerState: nextDatePickerState
        })
    );

    // clear date stored as disabled date attempted click
    store.dispatch(datePickerActions.disabledDayClickAttempt({disabledDayAttemptedClick: null}));
}

function selectedDatesSubmit(store) {
    const state = store.getState();
    const datePicker = state.datePickerReducer;
    const tripDetails = state.tripDetailsReducer;
    const arriveDateText = datePicker.arriveDateText;
    const departDateText = datePicker.departDateText;

    if (!datePickerStateChanged(datePicker, tripDetails)) {
        return;
    }

    const fieldsChanged = {
        arriveDateText,
        departDateText
    };

    // make sure we have both dates, or cleared dates
    if ((arriveDateText && departDateText) || (!arriveDateText && !departDateText)) {
        store.dispatch(tripActions.tripDetailsChanged(fieldsChanged));
    }
}

export default openDatePickerModal;
export {
    getNextOpenDatePickerState,
    getNextOpenModalState,
    getNextCloseModalState,
    checkGuests,
    closeDatePickerModal,
    openDatePickerModal,
    selectedDatesSubmit,
    changedSelectedDates
};
